// Mirrors the PRM Assist passenger app privacy policy
// (prmassist-app: src/scenes/other/privacy-policy/privacy-policy-content.ts).
// Keep the two in sync when the app policy changes.

export const LAST_UPDATED = 'October 7, 2026';

export const policySections = [
	// Introduction
	{
		body: 'PRM Assist sets out to offer users an easier and seamless alternative to the usual way of booking Passenger Reduced Mobility (PRM) services when travelling. PRM Assist Limited ("PRM Assist", "we", "us", "our") cares about your privacy. This Privacy Policy explains how we collect, use, store and protect your personal data when you use the PRM Assist mobile application and website.',
	},
	{
		body: 'By using the Service you agree to the collection and use of your information in accordance with this Privacy Policy. You have the right to refuse this Privacy Policy; however, this will mean you are unable to use our Service. You may withdraw your consent and delete your account at any time through the application settings.',
	},

	// Interpretation and Definitions
	{
		heading: 'Interpretation and Definitions',
	},
	{
		heading: 'Interpretation',
		body: 'Words with an initial capital letter have the meanings defined below. These definitions apply whether the terms appear in singular or plural.',
	},
	{
		heading: 'Definitions',
		body: 'For the purposes of this Privacy Policy:',
		bullets: [
			'Account means a unique account created for you to access our Service or parts of our Service.',
			'Affiliate means an entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest, or other securities entitled to vote for election of directors or other managing authority.',
			'Application means the mobile software program provided by the Company, named PRM Assist, downloaded by you on any electronic device.',
			'Company (referred to as "the Company", "we", "us", or "our" in this Policy) refers to PRM Assist Limited (company number 703260), Unit 6, Ringwood Centre, Damastown, Co. Dublin, Ireland.',
			'Country refers to the Republic of Ireland.',
			'Data Controller, for the purposes of the GDPR, refers to the Company as the legal person which alone or jointly with others determines the purposes and means of the processing of Personal Data.',
			'Device means any device that can access the Service, such as a mobile phone, computer, or digital tablet.',
			'GDPR means the General Data Protection Regulation (EU) 2016/679, the main piece of EU law governing your privacy rights.',
			'Personal Data is any information that relates to an identified or identifiable individual. Under GDPR this includes a name, identification number, location data, online identifier, or factors specific to your physical, physiological, genetic, mental, economic, cultural, or social identity.',
			'Service refers to the PRM Assist mobile application and, where applicable, the PRM Assist website.',
			'Service Provider means any natural or legal person who processes data on behalf of the Company. Service Providers are considered Data Processors under GDPR.',
			'SSR Code means a Special Service Request code. This is a standardised code (for example WCHR, WCHC, BLND, DEAF) representing the type of assistance you require when travelling.',
			'Usage Data refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, session duration or device diagnostics).',
			'Website refers to PRM Assist, accessible at www.prmassist.com.',
			'You means the individual accessing or using the Service, or the company or other legal entity on behalf of which such individual is accessing or using the Service. Under GDPR you may also be referred to as the Data Subject.',
		],
	},

	// Data We Collect
	{
		heading: 'Data We Collect',
	},
	{
		heading: 'Personal Data',
		body: 'When you create an account or use our Service, we may ask you to provide personally identifiable information including:',
		bullets: [
			'Email address',
			'First name and last name',
			'Phone number',
		],
	},
	{
		heading: 'SSR Codes',
		body: 'To arrange the correct level of assistance for your trip, you are asked to select an SSR Code representing your assistance requirement. SSR Codes may reveal information about a disability or reduced mobility and are therefore treated as potentially health-related data under GDPR Article 9. We collect SSR Codes only with your explicit consent. The SSR Code is linked to your account only for the duration of your trip. After you confirm completion of the trip, the code is anonymised (de-linked from your personal data) and retained only as an aggregate, non-identifiable statistic.',
	},
	{
		heading: 'Flight and Booking Details',
		body: 'To submit your PRM assistance request we collect flight numbers, travel dates, and airline reference (PNR) numbers. After trip completion, your personal information is removed from the booking record; the booking is retained only as an anonymised count.',
	},
	{
		heading: 'Location Data',
		body: 'With your consent, the app may collect your location data via Bluetooth beacons in airports, including when the app is closed or not in use. This data is used solely to calculate average airport waiting times. You can disable location permissions at any time through your device settings.',
	},
	{
		heading: 'Usage Data',
		body: 'Usage Data is collected automatically when you use the Service. This may include your device\'s IP address, device type and unique ID, operating system, the sections of the app you visit, the time and date of your visit, time spent on those sections, and other diagnostic data.',
	},
	{
		heading: 'Payment Data',
		body: 'We do not store or collect your payment card details. Payment information is provided directly to our third-party payment processor, Stripe, whose use of your personal information is governed by their Privacy Policy (https://stripe.com/privacy). Stripe adheres to the PCI-DSS standards managed by the PCI Security Standards Council.',
	},

	// How We Use Your Data
	{
		heading: 'How We Use Your Data',
		body: 'PRM Assist may use your Personal Data for the following purposes:',
		bullets: [
			'To provide and maintain the Service, including submitting PRM assistance requests to airlines and airports on your behalf.',
			'To manage your Account and your registration as a user of the Service.',
			'To process payments via Stripe for paid features within the Service.',
			'To contact you by email, push notifications, or other electronic communication regarding updates, security notices, or information related to the Service.',
			'To calculate airport waiting times using Bluetooth beacon data.',
			'To manage your support desk requests and tickets.',
			'To analyse usage trends and improve the Service, its features, and your experience.',
			'To provide you with news, special offers, and information about services similar to those you have used, unless you have opted out of marketing communications.',
			'For business transfers: to evaluate or conduct a merger, restructuring, or sale of assets in which your Personal Data may be among the assets transferred.',
		],
	},

	// Legal Basis for Processing
	{
		heading: 'Legal Basis for Processing (GDPR)',
		body: 'We process your Personal Data under the following lawful bases:',
		bullets: [
			'Performance of a contract: Account management, submitting your PRM assistance requests, and processing payments are necessary to deliver the Service you have contracted to use.',
			'Consent: We rely on your explicit consent for collecting SSR Codes (Article 9 health-related data), sending marketing communications, and collecting location data via Bluetooth beacons. You may withdraw consent at any time.',
			'Legitimate interest: We process Usage Data for analytics, service improvement, and security purposes where our legitimate interests are not overridden by your rights.',
			'Legal obligation: We may process data where required to comply with applicable laws.',
		],
	},

	// Sharing Your Data
	{
		heading: 'Sharing Your Data',
		body: 'We may share your personal information in the following situations:',
		bullets: [
			'With Service Providers: We share data with third-party Service Providers who act as Data Processors on our behalf, with appropriate Data Processing Agreements in place. This includes analytics providers, payment processors, and communication services.',
			'Payment processing: Your payment information is shared directly with Stripe for processing transactions.',
			'For business transfers: We may share or transfer your information in connection with a merger, acquisition, or sale of Company assets.',
			'Law enforcement: We may disclose your data if required by law or in response to valid requests by public authorities (for example, a court order or government agency request).',
			'With your consent: We may disclose your personal information for any other purpose with your explicit consent.',
		],
	},

	// Data Retention
	{
		heading: 'Data Retention',
		body: 'We retain your data for only as long as necessary for the purposes described in this Policy:',
		bullets: [
			'SSR Codes: Linked to your account only during an active trip. After trip completion the code is anonymised (de-linked from your identity) and retained only for aggregate statistics.',
			'Booking data: Your personal information is removed from booking records after trip completion. The booking is retained as an anonymised count.',
			'Account data: Retained while your account is active. Upon account deletion your personal data is removed in accordance with applicable law.',
			'Usage Data: Retained for a shorter period for analytics and service-improvement purposes, unless a longer retention is required for security or legal reasons.',
		],
	},

	// International Data Transfers
	{
		heading: 'International Data Transfers',
		body: 'Your information, including Personal Data, is processed at PRM Assist\'s operating offices in Ireland and by our Service Providers who may be located outside the European Economic Area (EEA). Where data is transferred outside the EEA, we ensure appropriate safeguards are in place, such as the European Commission\'s Standard Contractual Clauses or transfers to countries with an adequate level of data protection as determined by the European Commission.',
	},

	// Data Security
	{
		heading: 'Data Security',
		body: 'The security of your Personal Data is important to us. We use commercially acceptable means to protect your Personal Data. However, no method of transmission over the Internet or method of electronic storage is 100% secure, and we cannot guarantee its absolute security.',
	},

	// Children's Privacy
	{
		heading: 'Children\'s Privacy',
		body: 'You must be at least 16 years old to create an account. If you are under 16, a parent or guardian must create and manage the account on your behalf. We do not knowingly collect personally identifiable information from anyone under the age of 16 without parental consent. If you are a parent or guardian and you are aware that your child has provided us with Personal Data without your consent, please contact us at complaints@prmassist.com. If we become aware that we have collected Personal Data from anyone under 16 without verification of parental consent, we will take steps to remove that information from our servers.',
	},

	// Your Rights (GDPR)
	{
		heading: 'Your Rights under the GDPR',
		body: 'If you are within the EU/EEA, you have the following rights under GDPR:',
		bullets: [
			'Right of access: You may request a copy of the Personal Data we hold about you.',
			'Right to rectification: You may request correction of any incomplete or inaccurate information.',
			'Right to erasure: You may request deletion of your Personal Data where there is no legitimate reason for us to continue processing it.',
			'Right to restriction of processing: You may request that we restrict the processing of your Personal Data in certain circumstances.',
			'Right to data portability: You may request your Personal Data in a structured, commonly used, machine-readable format.',
			'Right to object: You may object to processing based on legitimate interest or for direct marketing purposes.',
			'Right to withdraw consent: You may withdraw your consent at any time. This does not affect the lawfulness of processing carried out before withdrawal.',
		],
	},
	{
		heading: 'Exercising Your Rights',
		body: 'To exercise any of these rights, please contact us at complaints@prmassist.com. We may ask you to verify your identity before responding. We will respond to your request within one month, as required by GDPR. You also have the right to lodge a complaint with the Irish Data Protection Commission (www.dataprotection.ie) or your local supervisory authority.',
	},

	// Cookies and Tracking
	{
		heading: 'Cookies and Tracking Technologies',
		body: 'The PRM Assist mobile application uses local storage and session tokens to authenticate you and remember your preferences. The PRM Assist website at www.prmassist.com may use cookies (small files placed on your device) for the following purposes:',
		bullets: [
			'Essential cookies: Required to provide core website functionality and authenticate your session.',
			'Analytics cookies: Used to understand how visitors interact with the website so we can improve it.',
		],
	},
	{
		body: 'You can instruct your browser to refuse all cookies or indicate when a cookie is being sent. However, some parts of the website may not function properly without cookies. The mobile application does not use browser cookies.',
	},

	// Third-Party Links
	{
		heading: 'Links to Other Websites',
		body: 'Our Service may contain links to other websites or services that are not operated by us. If you click on a third-party link, you will be directed to that third party\'s site. We strongly advise you to review the privacy policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.',
	},

	// Changes to This Policy
	{
		heading: 'Changes to This Privacy Policy',
		body: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy within the app and updating the "Last updated" date at the top. For material changes we will notify you via email and/or a prominent notice within the Service prior to the change becoming effective. Your continued use of the Service after changes are posted constitutes your acceptance of the updated Privacy Policy.',
	},

	// Contact Us
	{
		heading: 'Contact Us',
		body: 'If you have any questions about this Privacy Policy or wish to exercise your data protection rights, you can contact us by email at complaints@prmassist.com.',
	},
];
